/**
 * HMRCSettings.jsx
 * 
 * HMRC connection management and MTD status overview
 * 
 * Fraud prevention headers are automatically added by axiosConfig.js
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from './axiosConfig';
import {
    Box,
    Container,
    Card,
    CardContent,
    Typography,
    Button,
    Paper,
    CircularProgress,
    Alert,
    AlertTitle,
    Grid,
    TextField,
    Chip,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Snackbar
} from '@mui/material';
import {
    AccountBalance as HMRCIcon,
    Home as HomeIcon,
    CheckCircle,
    Error as ErrorIcon,
    Warning as WarningIcon,
    Link as LinkIcon,
    LinkOff as LinkOffIcon,
    Refresh as RefreshIcon,
    Receipt as ReceiptIcon,
    Schedule as ScheduleIcon,
    ArrowForward
} from '@mui/icons-material';

const HMRCSettings = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    
    const [loading, setLoading] = useState(true);
    const [connecting, setConnecting] = useState(false);
    const [connection, setConnection] = useState(null);
    const [obligations, setObligations] = useState([]);
    const [vrn, setVrn] = useState('');
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [disconnectDialog, setDisconnectDialog] = useState(false);
    const [checks, setChecks] = useState({}); // patch124

    useEffect(() => {
        // Check for OAuth callback result
        const callbackError = searchParams.get('error');
        const callbackSuccess = searchParams.get('success');
        
        if (callbackError) {
            setError(`HMRC connection failed: ${callbackError.replace(/_/g, ' ')}`);
        } else if (callbackSuccess === 'true') {
            setSuccess('Successfully connected to HMRC!');
        }
        
        fetchConnectionStatus();
    }, [searchParams]);

    const fetchConnectionStatus = async () => {
        try {
            setLoading(true);
            // Changed: use hmrcApi with relative path (baseURL is /api/hmrc)
            const res = await api.get('/hmrc/status');
            setConnection(res.data);
            
            if (res.data.vrn) {
                setVrn(res.data.vrn);
            }
            
            // Fetch obligations if connected
            if (res.data.connected) {
                fetchObligations();
            }
        } catch (err) {
            console.error('Failed to fetch HMRC status', err);
            setConnection({ connected: false, status: 'not_connected' });
        } finally {
            setLoading(false);
        }
    };

    // patch124: advisory pre-submission checks. Never blocks Submit; shows nothing if the call fails.
    const loadChecks = async (list) => {
        const open = (list || []).filter(o => o.status === 'O');
        const out = {};
        for (const o of open) {
            try {
                const r = await api.get(`/hmrc/obligations/${o.id}/pre-submission-checks`);
                out[o.id] = r.data.notices || [];
            } catch (err) {
                // advisory only
            }
        }
        setChecks(out);
    };

    const fetchObligations = async () => {
        try {
            // Changed: use hmrcApi with relative path
            const res = await api.get('/hmrc/obligations');
            const obligationList = res.data.obligations || [];
            setObligations(obligationList);
            loadChecks(obligationList);
        } catch (err) {
            console.error('Failed to fetch obligations', err);
        }
    };

    const handleConnect = async () => {
        if (!vrn || vrn.length !== 9 || !/^\d+$/.test(vrn)) {
            setError('Please enter a valid 9-digit VAT Registration Number');
            return;
        }
        
        try {
            setConnecting(true);
            setError(null);
            
            // Changed: use hmrcApi with relative path
            const res = await api.post('/hmrc/connect', { vrn });
            
            // Redirect to HMRC
            if (res.data.auth_url) {
                window.location.href = res.data.auth_url;
            }
        } catch (err) {
            console.error('Failed to initiate HMRC connection', err);
            setError(err.response?.data?.error || 'Failed to connect to HMRC');
            setConnecting(false);
        }
    };

    const handleDisconnect = async () => {
        try {
            // Changed: use hmrcApi with relative path
            await api.post('/hmrc/disconnect');
            setConnection({ connected: false, status: 'not_connected' });
            setObligations([]);
            setSuccess('HMRC disconnected successfully');
            setDisconnectDialog(false);
        } catch (err) {
            console.error('Failed to disconnect', err);
            setError('Failed to disconnect from HMRC');
        }
    };

    const getStatusChip = () => {
        if (!connection) return null;
        
        if (connection.connected) {
            return <Chip icon={<CheckCircle />} label="Connected" color="success" />;
        } else if (connection.status === 'expired') {
            return <Chip icon={<WarningIcon />} label="Expired" color="warning" />;
        } else if (connection.status === 'error') {
            return <Chip icon={<ErrorIcon />} label="Error" color="error" />;
        } else {
            return <Chip icon={<LinkOffIcon />} label="Not Connected" color="default" />;
        }
    };

    const openObligations = obligations.filter(o => o.status === 'O');
    const hasOverdue = openObligations.some(o => o.is_overdue);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #1e3a5f, #0d1b2a)',
                    color: 'white',
                    p: 4,
                    mb: 4,
                    borderRadius: 0
                }}
            >
                <Container maxWidth="lg">
                    <Button
                        startIcon={<HomeIcon />}
                        onClick={() => navigate('/dashboard')}
                        sx={{ color: 'white', mb: 2, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
                    >
                        Back to Dashboard
                    </Button>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ 
                                width: 56, 
                                height: 56, 
                                borderRadius: 2, 
                                bgcolor: 'rgba(255,255,255,0.2)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <HMRCIcon sx={{ fontSize: 28 }} />
                            </Box>
                            <Box>
                                <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    HMRC Making Tax Digital
                                </Typography>
                                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                                    Submit VAT returns directly to HMRC
                                </Typography>
                            </Box>
                        </Box>
                        {getStatusChip()}
                    </Box>
                </Container>
            </Paper>

            <Container maxWidth="lg">
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>
                )}

                <Grid container spacing={3}>
                    {/* Connection Card */}
                    <Grid item xs={12} md={6}>
                        <Card elevation={0} sx={{ border: '1px solid #e5e7eb', height: '100%' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                    <Box sx={{ 
                                        width: 40, 
                                        height: 40, 
                                        borderRadius: 2, 
                                        bgcolor: connection?.connected ? '#d1fae5' : '#fee2e2',
                                        color: connection?.connected ? '#059669' : '#dc2626',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        {connection?.connected ? <LinkIcon /> : <LinkOffIcon />}
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        HMRC Connection
                                    </Typography>
                                </Box>

                                {connection?.connected ? (
                                    <>
                                        <Box sx={{ bgcolor: '#f0fdf4', p: 2, borderRadius: 2, mb: 3 }}>
                                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                                VAT Registration Number
                                            </Typography>
                                            <Typography variant="h5" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                                                {connection.vrn}
                                            </Typography>
                                            {connection.organisation_name && (
                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                    {connection.organisation_name}
                                                </Typography>
                                            )}
                                        </Box>
                                        
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            Connected: {new Date(connection.connected_at).toLocaleDateString('en-GB')}
                                        </Typography>
                                        {connection.token_expires_at && (
                                            <Typography variant="body2" color="text.secondary">
                                                Token expires: {new Date(connection.token_expires_at).toLocaleString('en-GB')}
                                            </Typography>
                                        )}
                                        
                                        <Divider sx={{ my: 3 }} />
                                        
                                        <Box sx={{ display: 'flex', gap: 2 }}>
                                            <Button
                                                variant="outlined"
                                                startIcon={<RefreshIcon />}
                                                onClick={fetchObligations}
                                            >
                                                Refresh
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                startIcon={<LinkOffIcon />}
                                                onClick={() => setDisconnectDialog(true)}
                                            >
                                                Disconnect
                                            </Button>
                                        </Box>
                                    </>
                                ) : (
                                    <>
                                        <Alert severity="info" sx={{ mb: 3 }}>
                                            <AlertTitle>Connect to HMRC</AlertTitle>
                                            Link your HMRC account to submit VAT returns directly from SalonLogic Pro.
                                        </Alert>
                                        
                                        <TextField
                                            fullWidth
                                            label="VAT Registration Number"
                                            value={vrn}
                                            onChange={(e) => setVrn(e.target.value.replace(/\D/g, '').slice(0, 9))}
                                            placeholder="123456789"
                                            helperText="Enter your 9-digit VAT number"
                                            sx={{ mb: 3 }}
                                            inputProps={{ maxLength: 9 }}
                                        />
                                        
                                        <Button
                                            variant="contained"
                                            fullWidth
                                            startIcon={connecting ? <CircularProgress size={20} color="inherit" /> : <LinkIcon />}
                                            onClick={handleConnect}
                                            disabled={connecting || vrn.length !== 9}
                                            sx={{ 
                                                background: 'linear-gradient(135deg, #1e3a5f, #0d1b2a)',
                                                py: 1.5
                                            }}
                                        >
                                            {connecting ? 'Connecting...' : 'Connect to HMRC'}
                                        </Button>
                                        
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
                                            You'll be redirected to HMRC to sign in securely. We never see your Government Gateway password.
                                        </Typography>
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Obligations Card */}
                    <Grid item xs={12} md={6}>
                        <Card elevation={0} sx={{ border: '1px solid #e5e7eb', height: '100%' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                    <Box sx={{ 
                                        width: 40, 
                                        height: 40, 
                                        borderRadius: 2, 
                                        bgcolor: hasOverdue ? '#fee2e2' : '#dbeafe',
                                        color: hasOverdue ? '#dc2626' : '#3b82f6',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <ScheduleIcon />
                                    </Box>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            VAT Obligations
                                        </Typography>
                                        {connection?.connected && (
                                            <Typography variant="body2" color="text.secondary">
                                                {openObligations.length} open, {obligations.length - openObligations.length} fulfilled
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>

                                {!connection?.connected ? (
                                    <Alert severity="info">
                                        Connect to HMRC to view your VAT obligations
                                    </Alert>
                                ) : obligations.length === 0 ? (
                                    <Alert severity="info">
                                        No VAT obligations found. Click Refresh to fetch from HMRC.
                                    </Alert>
                                ) : (
                                    <List sx={{ p: 0 }}>
                                        {obligations.slice(0, 5).map((obl) => (
                                            <React.Fragment key={obl.id}>
                                            <ListItem
                                                key={obl.id}
                                                sx={{ 
                                                    px: 2, 
                                                    py: 1.5,
                                                    bgcolor: obl.status === 'O' ? (obl.is_overdue ? '#fef2f2' : '#fffbeb') : '#f0fdf4',
                                                    borderRadius: 2,
                                                    mb: 1
                                                }}
                                                secondaryAction={
                                                    obl.status === 'O' && (
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            onClick={() => navigate(`/vat/submit/${obl.id}`)}
                                                            sx={{ 
                                                                bgcolor: obl.is_overdue ? '#dc2626' : '#3b82f6',
                                                                '&:hover': { bgcolor: obl.is_overdue ? '#b91c1c' : '#2563eb' }
                                                            }}
                                                        >
                                                            Submit
                                                        </Button>
                                                    )
                                                }
                                            >
                                                <ListItemIcon sx={{ minWidth: 36 }}>
                                                    {obl.status === 'F' ? (
                                                        <CheckCircle sx={{ color: '#059669' }} />
                                                    ) : obl.is_overdue ? (
                                                        <ErrorIcon sx={{ color: '#dc2626' }} />
                                                    ) : (
                                                        <WarningIcon sx={{ color: '#d97706' }} />
                                                    )}
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={`${new Date(obl.period_start).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })} - ${new Date(obl.period_end).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}`}
                                                    secondary={obl.status === 'F' 
                                                        ? `Submitted ${obl.received_date ? new Date(obl.received_date).toLocaleDateString('en-GB') : ''}`
                                                        : `Due: ${new Date(obl.due_date).toLocaleDateString('en-GB')}`
                                                    }
                                                    primaryTypographyProps={{ fontWeight: 500 }}
                                                />
                                            </ListItem>
                                            {obl.status === 'O' && checks[obl.id] && checks[obl.id].length > 0 && (
                                                <Box component="details" sx={{ mb: 1, mt: -0.5, px: 2, py: 1, bgcolor: '#fffbeb', border: '1px solid #fde68a', borderRadius: 2 }}>
                                                    <Box component="summary" sx={{ cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', color: '#92400e' }}>
                                                        {checks[obl.id].length} thing{checks[obl.id].length === 1 ? '' : 's'} worth checking before you submit
                                                    </Box>
                                                    <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
                                                        {checks[obl.id].map((n) => (
                                                            <Typography key={n.code} component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                                {n.message}
                                                            </Typography>
                                                        ))}
                                                    </Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Prompts only, not tax advice. They do not stop you submitting.
                                                    </Typography>
                                                </Box>
                                            )}
                                            </React.Fragment>
                                        ))}
                                    </List>
                                )}
                                
                                {connection?.connected && obligations.length > 0 && (
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        endIcon={<ArrowForward />}
                                        onClick={() => navigate('/vat/history')}
                                        sx={{ mt: 2 }}
                                    >
                                        View All Submissions
                                    </Button>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Help Card */}
                    <Grid item xs={12}>
                        <Card elevation={0} sx={{ border: '1px solid #e5e7eb' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                    About Making Tax Digital
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={4}>
                                        <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                                What is MTD?
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Making Tax Digital requires VAT-registered businesses to keep digital records and submit VAT returns using compatible software.
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                                How it works
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                SalonLogic Pro calculates your VAT from your income and expenses, then submits directly to HMRC with one click.
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                                Your data is secure
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                We use HMRC's official OAuth system. Your Government Gateway credentials are never shared with us.
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Container>

            {/* Disconnect Confirmation Dialog */}
            <Dialog open={disconnectDialog} onClose={() => setDisconnectDialog(false)}>
                <DialogTitle>Disconnect from HMRC?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This will remove the connection to HMRC. You'll need to reconnect and re-authorise to submit VAT returns.
                        Your submission history will be preserved.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDisconnectDialog(false)}>Cancel</Button>
                    <Button onClick={handleDisconnect} color="error">Disconnect</Button>
                </DialogActions>
            </Dialog>

            {/* Success Snackbar */}
            <Snackbar
                open={!!success}
                autoHideDuration={5000}
                onClose={() => setSuccess(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="success" onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default HMRCSettings;